import type { Role } from '../rbac/roles.js'

/**
 * Content bounded context (TO-419) — versioned, translatable, approvable texts
 * (legal first) and their tenant/product assignments. Every value set is a
 * TypeScript enum stored as TEXT (never a Postgres ENUM).
 */

/** Lifecycle of one localization (one locale of one version). */
export enum ContentLocalizationStatus {
	DRAFT = 'draft',
	IN_REVIEW = 'in_review',
	APPROVED = 'approved',
	PUBLISHED = 'published',
	RETIRED = 'retired',
}

/** The five lifecycle actions; each appends one approval-track row. */
export enum ContentAction {
	SUBMIT = 'submit',
	APPROVE = 'approve',
	REJECT = 'reject',
	PUBLISH = 'publish',
	RETIRE = 'retire',
}

export enum ContentBodyFormat {
	MARKDOWN = 'markdown',
	PLAIN = 'plain',
}

/** Where a localization's wording came from (TO-100 adds `ai` producers later). */
export enum ContentTranslationOrigin {
	ORIGINAL = 'original',
	MANUAL = 'manual',
	AI = 'ai',
	IMPORT = 'import',
}

export enum ContentScopeType {
	TENANT = 'tenant',
	PRODUCT = 'product',
}

/** Where an assigned text renders. Tenant-scope texts are always `page_end`. */
export enum ContentPlacement {
	WITH_PRODUCT = 'with_product',
	PAGE_END = 'page_end',
}

/** Why a localization was retired (carried by `content.localization_retired`). */
export enum ContentRetireTrigger {
	MANUAL = 'manual',
	SUPERSEDED = 'superseded',
	VALIDITY_ENDED = 'validity_ended',
}

/** `actor_role` recorded on approval rows written by the daily job. */
export const CONTENT_SYSTEM_ACTOR_ROLE = 'SYSTEM'

/** Stable machine codes — the `error.message` prefix before the first `: `. */
export enum ContentErrorCode {
	VALIDATION_FAILED = 'CONTENT_VALIDATION_FAILED',
	IMMUTABLE_FIELD = 'CONTENT_IMMUTABLE_FIELD',
	TYPE_CODE_TAKEN = 'CONTENT_TYPE_CODE_TAKEN',
	TYPE_SYSTEM = 'CONTENT_TYPE_SYSTEM',
	TYPE_IN_USE = 'CONTENT_TYPE_IN_USE',
	TYPE_NOT_FOUND = 'CONTENT_TYPE_NOT_FOUND',
	TEXT_CODE_TAKEN = 'CONTENT_TEXT_CODE_TAKEN',
	LOCALE_EXISTS = 'CONTENT_LOCALE_EXISTS',
	REFERENCE_NOT_FOUND = 'CONTENT_REFERENCE_NOT_FOUND',
	BODY_UNSAFE = 'CONTENT_BODY_UNSAFE',
	LOCALIZATION_NOT_EDITABLE = 'CONTENT_LOCALIZATION_NOT_EDITABLE',
	INVALID_TRANSITION = 'CONTENT_INVALID_TRANSITION',
	PERMISSION_REQUIRED = 'CONTENT_PERMISSION_REQUIRED',
	APPROVER_ROLE_REQUIRED = 'CONTENT_APPROVER_ROLE_REQUIRED',
	SELF_APPROVAL_FORBIDDEN = 'CONTENT_SELF_APPROVAL_FORBIDDEN',
	REASON_REQUIRED = 'CONTENT_REASON_REQUIRED',
	VALIDITY_REQUIRED = 'CONTENT_VALIDITY_REQUIRED',
	VALIDITY_INVALID = 'CONTENT_VALIDITY_INVALID',
	VALIDITY_LOCKED = 'CONTENT_VALIDITY_LOCKED',
	PUBLISH_WINDOW_CONFLICT = 'CONTENT_PUBLISH_WINDOW_CONFLICT',
	TEXT_HAS_ASSIGNMENTS = 'CONTENT_TEXT_HAS_ASSIGNMENTS',
	TEXT_NOT_FOUND = 'CONTENT_TEXT_NOT_FOUND',
	ASSIGNMENT_SCOPE_INVALID = 'CONTENT_ASSIGNMENT_SCOPE_INVALID',
	REPLACEMENT_INVALID = 'CONTENT_REPLACEMENT_INVALID',
	ASSIGNMENT_EXISTS = 'CONTENT_ASSIGNMENT_EXISTS',
	ASSIGNMENT_IN_USE = 'CONTENT_ASSIGNMENT_IN_USE',
	TEXT_NOT_AVAILABLE = 'TEXT_NOT_AVAILABLE',
	SEED_FAILED = 'CONTENT_SEED_FAILED',
}

// ─── Response shapes (camelCase, ISO timestamps) ──────────────────────────────

export interface ContentTypeDTO {
	id: string
	code: string
	name: string
	description: string | null
	isSystem: boolean
	seedVersion: number | null
	requiresApproval: boolean
	approverRoles: Role[]
	forbidSelfApproval: boolean
	requiredLocales: string[]
	allowLocaleFallback: boolean
	createdAt: string
	updatedAt: string
}

export interface ContentLocalizationSummaryDTO {
	id: string
	locale: string
	status: ContentLocalizationStatus
	validFrom: string | null
	validTo: string | null
	contentSha256: string
}

export interface ContentVersionDTO {
	id: string
	textId: string
	versionNumber: number
	basedOnVersionId: string | null
	changeSummary: string | null
	createdBy: string
	createdAt: string
	localizations: ContentLocalizationSummaryDTO[]
}

export interface ContentTextDTO {
	id: string
	typeId: string
	typeCode: string
	code: string
	name: string
	description: string | null
	sourceLocale: string
	externalId: string | null
	createdBy: string
	createdAt: string
	updatedAt: string
	/**
	 * The current version number = the latest version's number (TO-630, AD-18).
	 * Derived, not a stored pointer: for texts, current = latest.
	 */
	currentVersion: number
	latestVersion: ContentVersionDTO
	missingLocales: string[]
}

/**
 * One published-in-force text localization as read over a delivery surface
 * (`GET /texts/live`, `GET /texts/{code}/live`, `POST /texts/query` — TO-630,
 * AD-19). Only live, published bodies reach this shape; DRAFT/PENDING/RETIRED
 * never do. `version` is the number of the version the live localization
 * belongs to.
 */
export interface LiveTextDTO {
	id: string
	code: string
	typeCode: string
	locale: string
	version: number
	title: string
	body: string
	bodyFormat: ContentBodyFormat
}

export interface ContentLocalizationDTO {
	id: string
	textId: string
	textCode: string
	versionId: string
	versionNumber: number
	locale: string
	title: string
	body: string
	bodyFormat: ContentBodyFormat
	contentSha256: string
	translationOrigin: ContentTranslationOrigin
	translatedFromId: string | null
	status: ContentLocalizationStatus
	validFrom: string | null
	validTo: string | null
	authoredBy: string
	approvedAt: string | null
	publishedAt: string | null
	retiredAt: string | null
	createdAt: string
	updatedAt: string
}

export interface ContentApprovalDTO {
	id: string
	localizationId: string
	action: ContentAction
	fromStatus: ContentLocalizationStatus
	toStatus: ContentLocalizationStatus
	userId: string | null
	actorRole: string
	reason: string | null
	validFrom: string | null
	validTo: string | null
	contentSha256: string
	createdAt: string
}

export interface ContentDiffDTO {
	localizationId: string
	againstLocalizationId: string
	title: { before: string; after: string; changed: boolean }
	body: { op: 'equal' | 'insert' | 'delete'; line: string }[]
}

export interface ContentInboxItemDTO {
	localizationId: string
	textId: string
	textCode: string
	typeCode: string
	versionNumber: number
	locale: string
	title: string
	authoredBy: string
	submittedBy: string
	submittedAt: string
}

export interface ContentAssignmentDTO {
	id: string
	textId: string
	textCode: string
	textName: string
	scopeType: ContentScopeType
	scopeId: string | null
	placement: ContentPlacement
	replacesTextId: string | null
	sortOrder: number
	createdBy: string
	createdAt: string
	updatedAt: string
}

export interface ContentProductTextsDTO {
	productId: string
	own: ContentAssignmentDTO[]
	inherited: (ContentAssignmentDTO & { replacedByTextId: string | null })[]
}

/** One resolved text for an offer (TO-429 maps it into the template payload). */
export interface ContentResolvedItemDTO {
	textId: string
	code: string
	title: string
	body: string
	bodyFormat: ContentBodyFormat
	locale: string
	versionNumber: number
	localizationId: string
	contentSha256: string
	productIds: string[]
}

export interface ContentResolutionDTO {
	locale: string
	at: string
	byProduct: Record<string, ContentResolvedItemDTO[]>
	pageEnd: ContentResolvedItemDTO[]
}

/** A text an offer carries that has no localization in force in the locale (TO-429). */
export interface ContentMissingTextDTO {
	code: string
	locale: string
	textId: string
	/** Requested products whose own assignment carries the text, ascending; `[]` = tenant-wide only (TO-102). */
	productIds: string[]
}

/** Outcome of an in-process content resolution (TO-429, ADR-0047 `ContentReadPort`). */
export enum ContentResolveStatus {
	RESOLVED = 'RESOLVED',
	TEXT_NOT_AVAILABLE = 'TEXT_NOT_AVAILABLE',
}
